import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { MediaItem } from '@/types';

interface HeroCarouselProps {
  items: MediaItem[];
}

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (items.length <= 1) return;
    const timer = setInterval(() => {
      setActive((v) => (v + 1) % items.length);
    }, 5500);
    return () => clearInterval(timer);
  }, [items.length]);

  if (items.length === 0) return null;

  const go = (dir: number) => {
    setActive((v) => (v + dir + items.length) % items.length);
  };

  return (
    <section className="relative w-full h-[480px] md:h-[600px] overflow-hidden">
      {items.map((item, index) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            index === active ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
        >
          <img
            src={item.banner}
            alt={`${item.title} 主视觉`}
            title={`${item.title} 影视资讯`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background-50 via-background-50/70 to-transparent"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-transparent to-transparent"></div>
        </div>
      ))}

      <div className="relative z-10 h-full w-full px-4 md:px-8 flex items-center">
        <div className="max-w-2xl w-full">
          {items.map(
            (item, index) =>
              index === active && (
                <div key={item.id} className="animate-fade-up">
                  <div className="flex items-center gap-2 mb-4">
                    <span className="px-2.5 py-1 rounded-md bg-accent-500 text-background-50 text-xs font-semibold">
                      本周热播
                    </span>
                    <span className="px-2.5 py-1 rounded-md bg-primary-500/80 text-foreground-950 text-xs">
                      {item.type === 'movie' ? '电影' : '电视剧'} · {item.year}
                    </span>
                    <span className="text-accent-400 text-sm font-semibold">
                      <i className="ri-star-fill mr-1"></i>
                      {item.rating.toFixed(1)}
                    </span>
                  </div>
                  <h1 className="font-heading text-3xl md:text-5xl font-extrabold text-foreground-950 leading-tight">
                    {item.title}
                  </h1>
                  <p className="mt-2 text-sm md:text-base text-foreground-500 tracking-wide">
                    {item.enTitle}
                  </p>
                  <p className="mt-4 text-sm md:text-base text-foreground-700 leading-relaxed line-clamp-2 md:line-clamp-3 max-w-xl">
                    {item.summary}
                  </p>
                  <div className="mt-5 flex flex-wrap gap-2">
                    {item.genres.map((g) => (
                      <span
                        key={g}
                        className="px-2.5 py-1 rounded-full bg-secondary-500/15 text-secondary-300 border border-secondary-500/30 text-xs"
                      >
                        {g}
                      </span>
                    ))}
                  </div>
                  <div className="mt-7 flex flex-wrap items-center gap-3">
                    <Link
                      to={`/detail/${item.id}`}
                      className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-accent-500 text-background-50 text-sm font-medium hover:bg-accent-600 transition-colors whitespace-nowrap"
                    >
                      <i className="ri-play-circle-line text-lg"></i>
                      查看影视详情
                    </Link>
                    <Link
                      to={`/category/${item.type}`}
                      className="inline-flex items-center gap-2 px-6 py-3 rounded-md border border-background-300/70 text-foreground-800 text-sm hover:border-accent-500/60 hover:text-accent-400 transition-colors whitespace-nowrap"
                    >
                      <i className="ri-compass-3-line"></i>
                      更多同类作品
                    </Link>
                  </div>
                </div>
              ),
          )}
        </div>
      </div>

      <div className="absolute z-20 bottom-6 right-4 md:right-8 flex items-center gap-3">
        <button
          type="button"
          onClick={() => go(-1)}
          aria-label="上一张"
          className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-background-100/80 backdrop-blur-sm border border-background-300/60 text-foreground-800 hover:text-accent-400 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-left-s-line text-lg"></i>
        </button>
        <div className="flex items-center gap-1.5">
          {items.map((item, index) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setActive(index)}
              aria-label={`切换到第 ${index + 1} 张`}
              className={`h-1.5 rounded-full transition-all cursor-pointer ${
                index === active ? 'w-6 bg-accent-500' : 'w-1.5 bg-background-400'
              }`}
            />
          ))}
        </div>
        <button
          type="button"
          onClick={() => go(1)}
          aria-label="下一张"
          className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-background-100/80 backdrop-blur-sm border border-background-300/60 text-foreground-800 hover:text-accent-400 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-right-s-line text-lg"></i>
        </button>
      </div>
    </section>
  );
}